import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";
import { useQuoteFilterStore } from "@/stores/modules/quotes/quotes-filter";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import { usePhotoFilterStore } from "@/stores/modules/photos/photo-filter";
import { useEventsStore } from "@/stores/modules/events/events";
import { useDilemmasStore } from "@/stores/modules/dilemmas/dilemmas";
import { useWordleStore } from "@/stores/modules/wordle/wordle";
import { useWordleScoresStore } from "@/stores/modules/wordle/wordle-scores";
import { useWordleMonthlyScoresStore } from "@/stores/modules/wordle/wordle-monthly-scores";
import { useWordleLeaderboardStore } from "@/stores/modules/wordle/wordle-leaderboard";
import { useWordle6Store } from "@/stores/modules/wordle6/wordle6";
import { useWordle6ScoresStore } from "@/stores/modules/wordle6/wordle6-scores";
import { useWordle6MonthlyScoresStore } from "@/stores/modules/wordle6/wordle6-monthly-scores";
import { useWordle6LeaderboardStore } from "@/stores/modules/wordle6/wordle6-leaderboard";
import { usePromptsStore } from "@/stores/modules/prompts/prompts";
import { useAppStore } from "@/stores/modules/app/app";
import { useToastStore } from "@/stores/modules/toast/toast";
import { useNotificationsStore } from "@/stores/modules/notifications/notifications";
import { useInAppNotificationsStore } from "@/stores/modules/notifications/in-app-notifications";
import { useDarkModeStore } from "@/stores/modules/darkmode/darkmode";
import { useQuotaStore } from "@/stores/modules/quota/quota";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { useShortUploadStore } from "@/stores/modules/shorts/short-upload";
import { useShortFilterStore } from "@/stores/modules/shorts/shorts-filter";
import { clearAllCachedImages } from "@/helpers/cache/image-cache";
import { clearAllCachedDocuments } from "@/helpers/cache/document-cache";
import { clearAllUploadJobs } from "@/helpers/cache/short-upload-queue";

/**
 * Resets all data in all Pinia stores
 * Used when user logs out
 */
export async function resetAllStoreData(): Promise<void> {
  // Reset app store
  const appStore = useAppStore();
  appStore.reset();

  // Reset auth store
  const authStore = useAuthStore();
  authStore.reset();

  // Reset user store
  const userStore = useUserStore();
  userStore.reset();

  // Reset groups store
  const groupsStore = useGroupsStore();
  groupsStore.reset();

  // Reset quotes store & filters
  const quotesStore = useQuotesStore();
  quotesStore.reset();
  const quoteFilterStore = useQuoteFilterStore();
  quoteFilterStore.clearAllFilters();

  // Reset photos store & filters
  const photosStore = usePhotosStore();
  photosStore.reset();
  const photoFilterStore = usePhotoFilterStore();
  photoFilterStore.clearAllFilters();

  // Reset shorts store & filters & upload
  const shortsStore = useShortsStore();
  shortsStore.reset();
  const shortUploadStore = useShortUploadStore();
  shortUploadStore.reset();
  const shortFilterStore = useShortFilterStore();
  shortFilterStore.clearAllFilters();

  // Reset events store
  const eventsStore = useEventsStore();
  eventsStore.reset();

  // Reset dilemmas store
  const dilemmasStore = useDilemmasStore();
  dilemmasStore.reset();

  // Reset wordle store
  const wordleStore = useWordleStore();
  wordleStore.reset();

  // Reset wordle scores store
  const wordleScoresStore = useWordleScoresStore();
  wordleScoresStore.reset();

  // Reset wordle monthly scores store
  const wordleMonthlyScoresStore = useWordleMonthlyScoresStore();
  wordleMonthlyScoresStore.reset();

  // Reset wordle leaderboard store
  const wordleLeaderboardStore = useWordleLeaderboardStore();
  wordleLeaderboardStore.reset();

  // Reset wordle6 store
  const wordle6Store = useWordle6Store();
  wordle6Store.reset();

  // Reset wordle6 scores store
  const wordle6ScoresStore = useWordle6ScoresStore();
  wordle6ScoresStore.reset();

  // Reset wordle6 monthly scores store
  const wordle6MonthlyScoresStore = useWordle6MonthlyScoresStore();
  wordle6MonthlyScoresStore.reset();

  // Reset wordle6 leaderboard store
  const wordle6LeaderboardStore = useWordle6LeaderboardStore();
  wordle6LeaderboardStore.reset();

  // Reset prompts store
  const promptsStore = usePromptsStore();
  promptsStore.reset();

  // Reset toast store
  const toastStore = useToastStore();
  toastStore.reset();

  // Reset push notifications store
  const notificationsStore = useNotificationsStore();
  notificationsStore.resetNotifications();

  // Reset in-app notifications store
  const inAppNotificationsStore = useInAppNotificationsStore();
  inAppNotificationsStore.cleanup();

  // Reset quota store
  const quotaStore = useQuotaStore();
  quotaStore.reset();

  // Reset darkmode store
  const darkModeStore = useDarkModeStore();
  darkModeStore.$reset();

  // Clear IndexedDB caches in parallel and await completion
  await Promise.all([
    clearAllCachedImages().catch((error) => {
      console.error("Error clearing image cache on logout:", error);
    }),
    clearAllCachedDocuments().catch((error) => {
      console.error("Error clearing document cache on logout:", error);
    }),
    clearAllUploadJobs().catch((error) => {
      console.error("Error clearing upload jobs on logout:", error);
    }),
  ]);
}
